/**
 * Base Pricing Calculator
 * 
 * Handles scalable base pricing calculations for different pricing types.
 * Supports fixed, per-person, per-day, and per-person-per-day pricing models.
 * 
 * @author Wildlife Project Team
 * @version 2.1.0
 */

import { BasePricingType, DEFAULT_VALUES } from './pricing-types';

/**
 * Calculate the scaled base price based on the pricing type and multipliers
 * 
 * @param basePrice - Original base price set by the business
 * @param basePriceType - How the base price should scale
 * @param peopleValue - Number of people (defaults to 1)
 * @param daysValue - Number of days (defaults to 1)
 * @returns Calculated base price after scaling
 */
export function calculateScalableBasePrice(
  basePrice: number,
  basePriceType: BasePricingType,
  peopleValue: number = DEFAULT_VALUES.PEOPLE,
  daysValue: number = DEFAULT_VALUES.DAYS
): number {
  switch (basePriceType) {
    case 'fixed':
      return basePrice; // No scaling - maintains backward compatibility
    case 'per_person':
      return basePrice * peopleValue;
    case 'per_day':
      return basePrice * daysValue;
    case 'per_person_day':
      return basePrice * peopleValue * daysValue;
    default:
      return basePrice; // Fallback to fixed pricing
  }
}

/**
 * Get a human-readable description for the base price calculation
 * 
 * @param basePriceType - The base pricing type
 * @param basePrice - Original base price
 * @param peopleValue - Number of people
 * @param daysValue - Number of days
 * @param currencySymbol - Currency symbol for display
 * @returns Descriptive text explaining the base price calculation
 */
export function getBasePriceDescription(
  basePriceType: BasePricingType,
  basePrice: number,
  peopleValue: number,
  daysValue: number,
  currencySymbol: string
): string {
  switch (basePriceType) {
    case 'fixed':
      return 'Fixed base price';
    case 'per_person':
      return `${currencySymbol}${basePrice} × ${peopleValue} ppl`;
    case 'per_day':
      return `${currencySymbol}${basePrice} × ${daysValue} days`;
    case 'per_person_day':
      return `${currencySymbol}${basePrice} × ${peopleValue} ppl × ${daysValue} days`;
    default:
      return 'Base price';
  }
}

/**
 * Get a business-friendly label for the base pricing type
 * 
 * @param basePriceType - The base pricing type
 * @returns Human-readable label
 */
export function getBasePriceTypeLabel(basePriceType: BasePricingType): string {
  switch (basePriceType) {
    case 'fixed': return 'Fixed Price';
    case 'per_person': return 'Per Person';
    case 'per_day': return 'Per Day';
    case 'per_person_day': return 'Per Person Per Day';
    default: return 'Fixed Price';
  }
}

/**
 * Validate base pricing configuration
 * 
 * @param basePrice - The base price to validate
 * @param basePriceType - The base pricing type to validate
 * @returns Object with validation result and any error message
 */
export function validateBasePricingConfig(
  basePrice: number,
  basePriceType: BasePricingType
): { isValid: boolean; error?: string } {
  if (basePrice < 0) {
    return { isValid: false, error: 'Base price cannot be negative' };
  }

  if (!['fixed', 'per_person', 'per_day', 'per_person_day'].includes(basePriceType)) {
    return { isValid: false, error: 'Invalid base pricing type' };
  }

  return { isValid: true };
} 